import { useCallback, useEffect, useRef, useState } from "react";
import { Galleria, GalleriaItemChangeEvent } from "primereact/galleria";
import { Button } from "primereact/button";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { selectViewImages } from "../../store/selectors/inspectionSelectors";
import { setViewImages } from "../../store/slices/inspectionSlice";
import { Asset } from "../../types/asset";
import "./QuestionImagesGalleria.css";

/**
 * Full-screen photo gallery for the Checklist Review / Surveyor Submission
 * tables. Deliberately mirrors the customer-portal Images.tsx gallery: a plain
 * (non-fullscreen) PrimeReact Galleria centred in a light overlay, so it reads
 * the same as the customer-facing viewer. The Defects page keeps its own
 * ImagesGalleria (PrimeReact fullScreen); this one is separate so the two don't
 * affect each other. Both read the shared inspection `viewImages` state.
 */
export const QuestionImagesGalleria = () => {
    const dispatch = useAppDispatch();
    const images = useAppSelector(selectViewImages);
    const [activeIndex, setActiveIndex] = useState(0);

    // objectURLs hold the decoded image in memory until revoked. An inspection
    // can have hundreds of photos, so free them as soon as the gallery closes
    // (or unmounts) rather than letting them accumulate for the whole session.
    // The presigned URLs stay cached, so re-opening is still cheap.
    const revokeObjectURLs = useCallback((assets: Asset[]) => {
        assets.forEach((asset) => {
            if (asset.objectURL) {
                window.URL.revokeObjectURL(asset.objectURL);
            }
        });
    }, []);

    const imagesRef = useRef(images);
    imagesRef.current = images;
    useEffect(() => () => revokeObjectURLs(imagesRef.current), [revokeObjectURLs]);

    const onHide = useCallback(() => {
        revokeObjectURLs(images);
        dispatch(setViewImages([]));
        setActiveIndex(0);
    }, [dispatch, images, revokeObjectURLs]);

    const itemTemplate = useCallback(
        (image: Asset) => (
            <img
                src={image.objectURL ?? image.url}
                alt={image.name}
                className="w-full"
                style={{ maxHeight: "80vh", objectFit: "contain" }}
            />
        ),
        []
    );

    const thumbnailTemplate = useCallback(
        (image: Asset) => (
            <img
                src={image.objectURL ?? image.url}
                alt={image.name}
                className="review-gallery-thumbnail"
                style={{ width: "80px" }}
            />
        ),
        []
    );

    const captionTemplate = useCallback(
        () => (
            <div className="review-gallery-info">
                {activeIndex + 1} of {images.length}
            </div>
        ),
        [activeIndex, images.length]
    );

    const onItemChange = useCallback(
        (event: GalleriaItemChangeEvent) => setActiveIndex(event.index),
        []
    );

    if (!images.length) {
        return null;
    }

    return (
        <div className="review-gallery-overlay" onClick={onHide}>
            <Button
                icon="pi pi-times"
                className="review-gallery-close"
                onClick={onHide}
                rounded
                text
            />
            <div className="review-gallery-container" onClick={(e) => e.stopPropagation()}>
                <Galleria
                    value={images}
                    activeIndex={activeIndex}
                    onItemChange={onItemChange}
                    item={itemTemplate}
                    thumbnail={thumbnailTemplate}
                    caption={captionTemplate}
                    numVisible={9}
                    showItemNavigators={images.length > 1}
                    showThumbnails={images.length > 1}
                    circular
                    style={{ width: "100%" }}
                />
            </div>
        </div>
    );
};
